/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import throttle from 'lodash.throttle'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Route } from 'react-router-dom'

import { MENU_HEIGHT, zIndex } from '../../components/css'
import GameHeaderSection from '../../components/GameHeaderSection'
import Icon from '../../components/Icon'
import { Container, Flex } from '../../components/Layout'
import { FixedTitle } from '../../components/PageTitle/css'
import { Desktop } from '../../components/Responsive'
import MobileMiniMatchup from './MobileMiniMatchup'

const getBackgroundImage = (
  genericSportForGame,
  isSoccer,
  imageName,
  isDesktop,
  sport = null
) => {
  if (!genericSportForGame) {
    if (isSoccer) {
      return '/images/soccer_game_background'
    } else {
      if (sport && sport?.name) {
        if (sport?.name === 'lacrosse') {
          return '/images/grass'
        }
        if (sport?.name === 'fieldhockey') {
          return '/images/grass'
        }
        if (sport?.name === 'floorball') {
          return '/images/floorball'
        }
        if (sport?.name === 'ballhockey') {
          return '/images/ballhockey'
        }
      }
      return '/images/game_hockey'
    }
  } else {
    return isDesktop
      ? genericSportForGame?.background_web_image?.replace('.png', '')
      : genericSportForGame?.background_mobile_image?.replace('.png', '')
  }
}

const GamePageHeader = ({
  actions: _actions,
  error,
  fullGameType,
  game,
  inFrame,
  organization,
  disabled,
  isSoccer = false,
  genericSportForGame = null,
  sport = null,
}) => {
  const headerRef = useRef(null)
  const [titleVisible, setTitleVisible] = useState(false)
  const HEIGHT = !game.permission?.update ? 340 : 340

  const actions = inFrame
    ? _actions && _actions.filter((action) => action && action.key === 'list')
    : _actions

  useEffect(() => {
    const listener = throttle(() => {
      const header = headerRef.current

      if (header) {
        const currScrollPos =
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          document.body.scrollTop
        let value = 1,
          threshold = HEIGHT - MENU_HEIGHT

        if (currScrollPos >= threshold) {
          value = 0
        } else if (currScrollPos > 0) {
          value = Math.min(
            1,
            Math.max(0, threshold - currScrollPos) / (1 * currScrollPos)
          )
        }

        header.style = `opacity: ${value}`

        // Also manage the visibility of the fixed title, because we broke the way
        // it normally works in PageHeader
        if (currScrollPos >= threshold) {
          setTitleVisible(true)
        } else {
          setTitleVisible(false)
        }
      }
    }, 10)

    document.addEventListener('scroll', listener)

    return () => {
      document.removeEventListener('scroll', listener)
    }
  }, [])

  let imageName = 'upcoming'
  if (game.completed) {
    imageName = 'completed'
  } else if (game.started) {
    imageName = 'in-progress'
  }

  const privateEntityError =
    (error && error.status === 403) || (error && error.status !== 404)

  let title = game.gameNumber
    ? `${t('util:capitalize', { text: t('common:game') })} #${game.gameNumber}`
    : game.gameNumber === null || game.gameNumber?.length === 0
    ? t('util:capitalize', { text: t('common:game') })
    : `${t('common:loading')}...`
  if (error && privateEntityError) {
    title = t('Web:private')
  }

  return (
    <Desktop>
      {(isDesktop) => {
        let imageUrl = ''
        if (!inFrame) {
          const backgroudImage = getBackgroundImage(
            genericSportForGame,
            isSoccer,
            imageName,
            isDesktop,
            sport
          )
          if (title !== `${t('common:loading')}...`) {
            imageUrl = backgroudImage
          }
        }
        return (
          <Container
            css={css`
              min-height: ${isDesktop ? 'auto' : HEIGHT - 80}px;
              padding-top: 24px;
              padding-bottom: 24px;
              position: relative;
              &:before {
                content: '';
                position: absolute;
                inset: 0;
                z-index: 0;
                background: rgba(0, 0, 0, 0.4);
                backdrop-filter: blur(5px);
                pointer-events: none;
              }
              ${imageUrl &&
              css`
                background-image: url('${imageUrl}.png');
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
              `}
              ${isDesktop
                ? css`
                    height: 100%;
                    margin-bottom: 8px;
                  `
                : css`
                    padding: 0 0 ${HEIGHT - 80}px;
                  `}
            `}
            wrapCss={css`
              height: 100%;
            `}
          >
            <Helmet title={title} />
            {!isDesktop && (
              <FixedTitle noFlex justifyContent='center' visible={titleVisible}>
                <div>
                  <h2>{title}</h2>
                </div>
              </FixedTitle>
            )}

            {isDesktop && disabled && (
              <Route exact path='/game/:game/timeline'>
                <>
                  {/* Overlay covering the Container area only */}
                  <div
                    css={css`
                      position: absolute;
                      inset: 0;
                      z-index: ${zIndex.header - 1};
                      background-color: rgba(0, 0, 0, 0.77);
                      cursor: not-allowed;
                    `}
                  />
                  {/* Warning message centered in Container */}
                  <Flex
                    column
                    alignItems='center'
                    justifyContent='center'
                    css={css`
                      position: absolute;
                      inset: 0;
                      z-index: ${zIndex.header};
                      pointer-events: none;
                      font-size: 20px;
                      color: ${colors.DEFAULT_FLAIR};
                    `}
                  >
                    <Icon
                      name='exclamation-triangle'
                      fontSize={40}
                      css={css`
                        margin-bottom: 20px;
                      `}
                      color={colors.DEFAULT_FLAIR}
                    />
                    Please do not leave the page while editing the game
                    timeline.
                    <p
                      css={css`
                        margin-top: 4px;
                      `}
                    >
                      Press the &apos;Save Timeline&apos; button to exit.
                    </p>
                  </Flex>
                </>
              </Route>
            )}
            <div
              css={css`
                position: relative;
                z-index: 1;
                width: 100%;
              `}
            >
              <GameHeaderSection
                ref={headerRef}
                organization={organization}
                game={game}
                title={title}
                fullGameType={fullGameType}
                isDesktop={isDesktop}
                isSoccer={isSoccer}
                error={error}
              />
            </div>
            {!isDesktop && (
              <MobileMiniMatchup game={game} isSticky={titleVisible} />
            )}
          </Container>
        )
      }}
    </Desktop>
  )
}

GamePageHeader.propTypes = {
  actions: PropTypes.node,
  game: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired,
}

export default GamePageHeader
